import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SubuserPermissionsService, PolicySummary } from '@services/subuser-permissions.service';

export type PolicyStepKey = 'pages' | 'roles' | 'permissions' | 'users';

interface StepDef {
  key: PolicyStepKey;
  route: string;
  icon: string;
  /** i18n keys */
  title: string;
  hint: string;
}

/**
 * The Policy Management flow, shown as one strip on every screen in the chain.
 *
 * Access is built in four steps and each one depends on the previous, which is
 * hard to see when the screens are visited on their own:
 *
 *   1. Pages        — what can be controlled at all (client_pages)
 *   2. Roles        — the named policies (client_roles)
 *   3. Permissions  — which pages each role may open (client_role_permissions)
 *   4. Sub-Users    — who carries each role (assign_subuser_role)
 *
 * The counts come from one shared snapshot in the service, so the same number is
 * shown on every screen rather than each screen counting for itself.
 */
@Component({
  selector: 'app-policy-flow-steps',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './policy-flow-steps.component.html',
  styleUrls: ['./policy-flow-steps.component.scss'],
})
export class PolicyFlowStepsComponent implements OnInit {
  /** Which step the current screen represents. */
  @Input() active: PolicyStepKey = 'pages';

  private permissionsService = inject(SubuserPermissionsService);
  private router = inject(Router);

  summary: PolicySummary | null = null;

  readonly steps: StepDef[] = [
    { key: 'pages',       route: '/home/client-pages',     icon: 'fa-solid fa-file-lines',   title: 'policy-flow.step-pages',       hint: 'policy-flow.step-pages-hint' },
    { key: 'roles',       route: '/home/roles',            icon: 'fa-solid fa-users-gear',   title: 'policy-flow.step-roles',       hint: 'policy-flow.step-roles-hint' },
    { key: 'permissions', route: '/home/roles-management', icon: 'fa-solid fa-sliders',      title: 'policy-flow.step-permissions', hint: 'policy-flow.step-permissions-hint' },
    { key: 'users',       route: '/home/user-managment',   icon: 'fa-solid fa-user-shield',  title: 'policy-flow.step-users',       hint: 'policy-flow.step-users-hint' },
  ];

  ngOnInit(): void {
    this.permissionsService.policySummary$.subscribe((s) => (this.summary = s));
    // Uses the shared snapshot; only the first screen in a session pays for it.
    this.permissionsService.getPolicySummary().subscribe({
      error: () => { /* the strip degrades to titles only */ },
    });
  }

  /** The count badge for a step, or null while the snapshot is still loading. */
  countFor(key: PolicyStepKey): number | null {
    if (!this.summary) return null;
    switch (key) {
      case 'pages': return this.summary.pagesCount;
      case 'roles': return this.summary.rolesCount;
      // Roles that actually grant something. A role with zero pages is created
      // but not yet configured, which is the thing worth surfacing here.
      case 'permissions':
        return Object.values(this.summary.pagesPerRole).filter((n) => n > 0).length;
      case 'users': return this.summary.subUsersCount;
    }
  }

  isActive(key: PolicyStepKey): boolean {
    return this.active === key;
  }

  /** Steps before the current one are done; used only for the connector styling. */
  isBefore(key: PolicyStepKey): boolean {
    const order = this.steps.map((s) => s.key);
    return order.indexOf(key) < order.indexOf(this.active);
  }

  go(step: StepDef): void {
    if (this.isActive(step.key)) return;
    // Permissions needs a role to configure — send the user to pick one when
    // none exists yet, rather than opening an empty grid.
    if (step.key === 'permissions' && this.summary && this.summary.rolesCount === 0) {
      this.router.navigate(['/home/roles']);
      return;
    }
    this.router.navigate([step.route]);
  }

  /** Shown as a warning when sub-users exist that no role covers. */
  get unassignedWarning(): number {
    return this.summary?.unassignedCount || 0;
  }
}
